//Frontend/src/unicodex_ui/pages/ManufacturerCodesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalApi from "../services/portalApi";
import "../styles/ManufacturerPortal.css";

function ManufacturerCodesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [products, setProducts] = useState([]);

  const [count, setCount] = useState("100");
  const [generating, setGenerating] = useState(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const navigate = useNavigate();

  function signOut() {
    localStorage.removeItem("ucx_mfg_token");
    localStorage.removeItem("ucx_mfg_user");
    navigate("/portal/login", { replace: true });
  }

  const selectedBatch = useMemo(
    () => batches.find((b) => String(b.id) === String(batchId)) || null,
    [batches, batchId]
  );

  const selectedProductForBatch = useMemo(() => {
    if (!selectedBatch) return null;
    const pid = selectedBatch.product_id;
    return products.find((p) => String(p.id) === String(pid)) || null;
  }, [selectedBatch, products]);
  
  const selectedBarcode = selectedProductForBatch?.barcode_value || selectedProductForBatch?.barcodeValue || "";


  async function loadBatches() {
    const res = await portalApi.listBatches();
    if (!res?.ok) throw new Error("batches_failed");
    const bs = Array.isArray(res.batches) ? res.batches : [];
    setBatches(bs);
    if (!batchId && bs[0]) setBatchId(String(bs[0].id));
    return bs;
  }

  async function loadProducts() {
    const res = await portalApi.listProducts();
    if (!res?.ok) throw new Error("products_failed");
    const ps = Array.isArray(res.products) ? res.products : [];
    setProducts(ps);
    return ps;
  }


  async function loadCodes(nextBatchId = batchId, nextOffset = offset) {
    if (!nextBatchId) return;

    setLoading(true);
    setError("");

    try {
      const res = await portalApi.listCodes(Number(nextBatchId), {
        limit,
        offset: nextOffset,
      });

      if (!res?.ok) throw new Error("codes_failed");

      setRows(Array.isArray(res.rows) ? res.rows : []);
      setTotal(Number(res.total || 0));
    } catch (e) {
      console.error("[MfgCodes] load error:", e);
      setError("Unable to load codes for this batch.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function init() {
    setLoading(true);
    setError("");
    try {
      const [bs] = await Promise.all([loadBatches(), loadProducts()]);
      const effective = batchId || (bs[0] ? String(bs[0].id) : "");
      if (effective) {
        setOffset(0);
        await loadCodes(effective, 0);
      }
    } catch (e) {
      console.error("[MfgCodes] init error:", e);
      setError("Unable to load batches.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!batchId) return;
    setOffset(0);
    loadCodes(batchId, 0).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, limit]);

  useEffect(() => {
    if (!batchId) return;
    loadCodes(batchId, offset).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  async function generate() {
    if (!batchId) return;
    const n = Number(String(count).replace(/[^\d]/g, ""));
    if (!n || n <= 0) {
      setError("Enter a valid count.");
      return;
    }

    setGenerating(true);
    setError("");
    try {
      const res = await portalApi.generateCodes(Number(batchId), n);
      if (!res?.ok) throw new Error("generate_failed");
      setOffset(0);
      await loadCodes(batchId, 0);
    } catch (e) {
      console.error("[MfgCodes] generate error:", e);
      setError("Unable to generate codes for this batch.");
    } finally {
      setGenerating(false);
    }
  }

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <section className="ucx-section ucx-mfg-portal-page">
      <div className="ucx-container">
        <ManufacturerNav title="Manufacturer Portal" subtitle="Codes" onSignOut={signOut} />

        <div className="ucx-mfg-panel">
          <div className="ucx-mfg-panel-head">
            <div>
              <h1 className="ucx-mfg-title">Codes</h1>
              <p className="ucx-mfg-subtitle">Generate and view UNI-CODEX codes per batch.</p>
            </div>
          </div>

          {error && <div className="ucx-mfg-error">{error}</div>}

          <div className="ucx-mfg-filters">
            <select className="ucx-mfg-select" value={batchId} onChange={(e) => setBatchId(e.target.value)}>
              {batches.map((b) => {
                const p = products.find((x) => String(x.id) === String(b.product_id));
                const bc = p?.barcode_value || p?.barcodeValue || "—";
                return (
                  <option key={b.id} value={b.id}>
                    #{b.id} — {b.product_name || b.product_id} — {b.ucx_batch || "—"} — barcode: {bc}
                  </option>
                );
              })}

            </select>

            <div className="ucx-mfg-inline">
              <input
                className="ucx-mfg-input"
                value={count}
                onChange={(e) => setCount(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="Count"
              />
              <button type="button" className="ucx-btn ucx-btn-primary" onClick={generate} disabled={generating || !batchId}>
                {generating ? "Generating…" : "Generate"}
              </button>
            </div>

            <div className="ucx-mfg-inline">
              <select className="ucx-mfg-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>

              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
                disabled={!canPrev || loading}
              >
                Prev
              </button>
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => setOffset((o) => o + limit)}
                disabled={!canNext || loading}
              >
                Next
              </button>
            </div>
          </div>

          <div className="ucx-mfg-note">
            Batch: <b>{selectedBatch ? `#${selectedBatch.id}` : "—"}</b>
            {" "}
            • Product barcode: <b>{selectedBarcode || "—"}</b>
            {selectedBatch && !selectedBarcode ? (
              <span className="ucx-mfg-badge-warn" style={{ marginLeft: 8 }}>
                Add barcode in Products
              </span>
            ) : null}
            {" "}
            • Showing{" "}

            <b>{total === 0 ? 0 : Math.min(total, offset + 1)}</b>–<b>{Math.min(total, offset + limit)}</b> of{" "}
            <b>{total}</b>
          </div>

          <div className="ucx-mfg-tablewrap">
            <table className="ucx-mfg-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>Last verified</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="ucx-mfg-empty">Loading…</td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="ucx-mfg-empty">No codes found for this batch.</td>
                  </tr>
                )}

                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td className="ucx-mfg-mono">{c.code}</td>
                    <td>{c.status || "-"}</td>
                    <td>{c.verified_count ?? 0}</td>
                    <td>{c.last_verified_at ? new Date(c.last_verified_at).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ucx-mfg-note">
            Tip: copy a code and test it on the public <b>/verify</b> page.
          </div>
        </div>
      </div>
    </section>
  );
}

export default ManufacturerCodesPage;
